import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import moment from 'moment';
import {
  toLocalDateFromInstant,
  toLocalDateFromPickerOrImport
} from 'src/util/partialDate';

// THESE SIX ARE RETAINED ONLY FOR THE DEAD *_Notification.jsx FORMS
// (TGCLOUD-1784). Every live picker now reads its values from the configured
// list, so a site can add to them and switch entries off. The four files still
// importing these have no importers of their own and cannot be reached; the
// exports stay so they keep compiling until someone decides to delete them.
// Do not wire a new picker to one of these arrays.
export const REFERRAL_REASONS = [
  { label: 'Personal history', value: 'personal_history' },
  { label: 'Family history', value: 'family_history' },
  { label: 'Suspected diagnosis', value: 'suspected_diagnosis' },
  { label: 'Carrier testing', value: 'carrier_testing' },
  { label: 'Predictive testing', value: 'predictive_testing' },
  { label: 'Advanced maternal age', value: 'advanced_maternal_age' }
];

export const REFERRAL_TRIAGE = [
  { label: 'Immediate', value: 'immediate' },
  { label: 'Urgent', value: 'urgent' },
  { label: 'Semi-urgent', value: 'semi_urgent' },
  { label: 'Standard', value: 'standard' }
];

export const CONTACT_TYPES = [
  { label: 'Contact with patient', value: 'contact_with_patient' },
  {
    label: 'Contact with patient’s doctor',
    value: 'contact_with_patient_doctor'
  },
  { label: 'Post clinic follow-up', value: 'post_clinic_follow_up' },
  { label: 'Pre-clinic request', value: 'pre_clinic_request' },
  { label: 'Case review', value: 'case_review' },
  { label: 'Risk notification', value: 'risk_notification' }
];

export const SAMPLE_TYPES = [
  { label: 'Blood', value: 'blood' },
  { label: 'Buccal swab', value: 'buccal_swab' },
  { label: 'Saliva', value: 'saliva' },
  { label: 'Hair', value: 'hair' },
  { label: 'Prepared DNA extract', value: 'prepared_dna_extract' }
];

export const CONSENT_GIVEN_BY = [
  { label: 'Self', value: 'self' },
  { label: 'Parent', value: 'parent' },
  { label: 'Child', value: 'child' },
  { label: 'Other relative', value: 'other_relative' },
  { label: 'Guardian', value: 'guardian' },
  { label: 'Power of Attorney', value: 'power_of_attorney' }
];

export const CONSENT_RESPONSE = [
  { label: 'Consent given', value: 'consent_given' },
  { label: 'Denied consent', value: 'denied_consent' },
  { label: 'Partial consent', value: 'partial_consent' },
  { label: 'Withdrawn consent', value: 'withdrawn_consent' },
  { label: 'No response', value: 'no_response' }
];

export const VARIANT_INTERPRETATION_OPTIONS = [
  { label: 'Pathogenic', value: 'Pathogenic' },
  { label: 'Likely Pathogenic', value: 'Likely Pathogenic' },
  {
    label: 'Variant of Uncertain Significance',
    value: 'Variant of Uncertain Significance'
  },
  { label: 'Likely Benign', value: 'Likely Benign' },
  { label: 'Benign', value: 'Benign' }
];

// Whether a variant was inherited or acquired.
//
// This is not a cosmetic detail: a somatic variant cannot be transmitted, so
// it is not evidence of a Mendelian cause. With no way to record it, the
// pedigree analyzer had to treat every pathogenic variant in an affected
// person as heritable, which is how a somatic JAK2 driver on a myelofibrosis
// patient was reported as autosomal dominant vertical transmission.
//
// "Not stated" is the default and is deliberately NOT the same as germline:
// the analyzer treats it as potentially heritable (matching how records
// created before this field existed already behave), so somatic has to be
// stated positively to change anything.
export const VARIANT_ORIGIN_OPTIONS = [
  { label: 'Not stated', value: 'unknown' },
  { label: 'Germline (inherited)', value: 'germline' },
  { label: 'Somatic (acquired)', value: 'somatic' }
];

// THE DROP-DOWN VALUES MOVED OUT (TGCLOUD-1784).
//
// These three helpers used to read and write one Mixed blob on the group
// preferences document, with the shipped values hard-coded just above them.
// Both now live in the lookup values collection: the values are rows a site
// can enable, disable, order and default, and what ships is declared once in
// `backend/config/lookupDefinitions.js` rather than duplicated here.
//
// They are re-exported under their old names, with their old signatures,
// because nineteen files import them. Each picker moves to `useLookupList`
// and `useLookupMutations` in its own commit rather than all at once.
//
// Two things behave differently behind the same names, both deliberate. The
// delete now DISABLES the value rather than removing it, so it keeps its
// place for when someone adds it back and every record holding it is
// untouched. And the server enforces the role rule for the first time: a
// genetics user calling the save gets a 403 instead of a silent write, which
// the interface has always implied but nothing enforced.
export {
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from 'src/util/lookups/lookupsApi';

const getReferralTags = async (theme, memberId, familyId, response) => {
  if (!response)
    response = await axios.get(APICONSTANTS.referral, {
      params: { familyId }
    });

  let referralOptions = response.data;
  referralOptions = referralOptions.filter((referral) => {
    return referral.familyMemberId == memberId || referral.showInFamily;
  });
  let referralTags = [];
  for (const referral of referralOptions) {
    referralTags.push({
      _id: referral._id,
      label:
        referral.referredBy +
        ' ' +
        moment(referral.createdDate).format(theme.dateFormat)
    });
  }
  return referralTags;
};

const getConsentTags = async (theme, memberId, familyId, response) => {
  if (!response)
    response = await axios.get(APICONSTANTS.consent, {
      params: { familyId }
    });
  let options = response.data;
  options = options.filter((option) => {
    return option.familyMemberId == memberId;
  });
  let Tags = [];
  for (const option of options) {
    Tags.push({
      _id: option._id,
      label:
        option.consentType +
        ' ' +
        moment(option.createdDate).format(theme.dateFormat)
    });
  }
  return Tags;
};

const getDependencies = async (familyId, _id, dateFormat) => {
  let dependences = [];
  await axios
    .get(`/clinical-notes/all`, {
      params: { familyId }
    })
    .then((res) => {
      for (const note of res.data) {
        if (
          note.associatedReferral?._id == _id ||
          note.associatedConsent?._id == _id
        ) {
          dependences.push(
            note.noteType +
              ' created ' +
              moment(note.createdAt).format(dateFormat)
          );
        }
      }
    });
  return dependences;
};

//Keeping these here just in case I need them, for now they need fixing which I have no reason to do
//const getAppointmentTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.appointment}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.clinicName + ' ' + option.appointmmentDate.split('T')[0]);
//  }
//  return Tags;
//};

//const getContactTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.contact}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.contactType + ' ' + option.contactDate.split('T')[0]);
//  }
//  return Tags;
//};
//
//
//const getSampleTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.sample}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.sampleType + ' ' + option.dateTaken.split('T')[0]);
//  }
//  return Tags;
//};
//
//const getTestTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.test}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.testName + ' ' + option.resultDueDate.split('T')[0]);
//  }
//  return Tags;
//};
//
//const getAddressTags = async (response) => {
//  if (!response) response = await axios.get(`/api/${APICONSTANTS.address}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.postCode + ' ' + option.town);
//  }
//  return Tags;
//};
//
//const getClinicalNoteTags = async (response) => {
//  if (!response)
//    response = await axios.get(`/api/${APICONSTANTS.clinicalNote}`);
//  const options = response.data;
//  let Tags = [];
//  for (const option of options) {
//    //Consent has no name so I couldn't use that
//    Tags.push(option.clinician + ' ' + option.date.split('T')[0]);
//  }
//  return Tags;
//};

const getAddressFlags = async ({ familyId, familyMemberId }) => {
  const res = await axios.get(
    APICONSTANTS.fetchAddressFlags
      .replace(':familyId', familyId)
      .replace(':familyMemberId', familyMemberId)
  );

  return res.data || {};
};

// The field a record is dated by. A Test is dated by its date requested, or
// failing that its date received; a Consent by its consent date, or failing
// that its request date. A record type with no date of its own is dated by when
// it was created.
const recordDateField = (record) => {
  switch (record.noteType) {
    case 'Appointment':
      return 'appointmentDate';
    case 'Referral':
      return 'referralDate';
    case 'Test':
      return record.dateRequested ? 'dateRequested' : 'dateResultReceived';
    case 'Sample':
      return 'dateTaken';
    case 'Clinical Note':
      return 'clinicalNoteDate';
    case 'Contact':
      return 'contactDate';
    case 'Consent':
      return record.consentDate ? 'consentDate' : 'consentRequestDate';
    case 'Treatment':
      return 'treatmentDate';
    case 'Tissue Pathology':
      return 'pathReportDate';
    case 'Process':
      return 'processStartDate';
    case 'Disease':
      return 'dateReported';
    case 'Phenotype':
    case 'Genotype':
      return 'dateRecorded';
    default:
      // Risk Score, Pregnancy and anything else.
      return 'createdAt';
  }
};

export const getRecordDate = (record) => record[recordDateField(record)];

/**
 * The record's date (getRecordDate) as a local calendar day, read by how it
 * was WRITTEN (CLAUDE.md, Dates) — for display, and for anything else that
 * needs the day rather than the instant.
 *
 *   `createdAt`, a server timestamp      -> a true instant -> toLocalDateFromInstant
 *   every clinician-picked record date   -> mixed          -> toLocalDateFromPickerOrImport
 *     (the form's picker writes a local-anchored instant, the spreadsheet
 *      importers `new Date('YYYY-MM-DD')`, i.e. UTC midnight)
 *
 * `moment(value)` read every one of them by its local parts, so an imported
 * date showed a day early at any negative offset.
 *
 * @param {object} record
 * @returns {Date|null} null when the record has no readable date
 */
export const readRecordDate = (record) => {
  const field = recordDateField(record);
  return field === 'createdAt'
    ? toLocalDateFromInstant(record.createdAt)
    : toLocalDateFromPickerOrImport(record[field]);
};

export const getRecordTitle = (record) => {
  switch (record.noteType) {
    case 'Appointment':
      return record.clinicName || record.appointmentType || 'Appointment';
    case 'Referral':
      return (
        record.referralReason ||
        record.referralType ||
        (record.referredBy ? `Referral by ${record.referredBy}` : 'Referral')
      );
    case 'Test':
      return record.testName || record.testType || 'Test';
    case 'Sample':
      return record.sampleType || record.trackingId || 'Sample';
    case 'Clinical Note':
      return (
        (record.note || '').split('\n')[0].slice(0, 100) || 'Clinical Note'
      );
    case 'Contact':
      return record.reasonForContact || record.contactType || 'Contact';
    case 'Consent':
      return record.consentType || 'Consent';
    case 'Treatment':
      return record.treatment || 'Treatment';
    case 'Pregnancy':
      return 'Pregnancy';
    case 'Address':
      return (
        [record.addressLine1, record.town || record.city]
          .filter(Boolean)
          .join(', ') || 'Address'
      );
    case 'Tissue Pathology':
      return (
        [record.tissueSite, record.morphology].filter(Boolean).join(' — ') ||
        'Tissue Pathology'
      );
    case 'Process':
      return record.processType || 'Process';
    case 'Disease':
      return record.name || 'Disease';
    case 'Phenotype':
      return record.name || 'Phenotype';
    case 'Genotype':
      return record.name || 'Genotype';
    case 'Risk Score':
      return record.riskLabel || 'Risk Score';
    default:
      return record.noteType || 'Record';
  }
};

export const getSearchableText = (record) =>
  [
    getRecordTitle(record),
    record.noteType,
    record.note,
    record.referralReason,
    record.referralNotes,
    record.appointmentNotes,
    record.testNotes,
    record.testResult,
    record.testName,
    record.consentNotes,
    record.contactNotes,
    record.comment,
    record.reasonForContact,
    record.sampleType,
    record.sampleStatus,
    record.treatment,
    record.referralType,
    record.referralTo,
    record.referredBy,
    record.clinicName,
    record.clinician,
    record.testType,
    record.addressLine1,
    record.addressLine2,
    record.town,
    record.city,
    record.postCode,
    record.country,
    record.laboratory,
    record.tissueSite,
    record.morphology,
    record.consentType,
    record.contactType,
    record.id,
    record.carrierStatus,
    record.riskLabel,
    record.gailRiskScore != null ? String(record.gailRiskScore) : null,
    record.prsResult?.trait,
    record.prsResult?.pgsId
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export {
  getAddressFlags,
  getConsentTags,
  getReferralTags,
  getDependencies
  //getAddressTags,
  //getClinicalNoteTags,
  //getTestTags,
  //getSampleTags,
  //getContactTags,
  //getAppointmentTags
};
